import {
    AppRegistration,
    Email as EmailIcon,
    Foundation,
    Info as InfoIcon,
    Login
} from "@mui/icons-material";
import SecurityIcon from '@mui/icons-material/Security';
import {
    Box,
    useMediaQuery,
    useTheme
} from "@mui/material";
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { getTenantSlug } from "src/allauth/lib/django";
import { useAppContext } from "src/context/AppContext";
import { MenuButton } from "src/theme/StyledFields";


export type MenuLayout = 'drawer' | 'footer' | 'header';

export interface MenuProps {
    layout?: MenuLayout;
    isAuthenticated?: boolean;
}

export const getDrawerClickHandler = (
    layout: MenuLayout,
    closeDrawer: () => void,
): (() => void) | undefined => {
    if (layout === 'drawer') {
        return closeDrawer;
    }
    return undefined;
};

export const getMenuWrapperSx = (layout: MenuLayout = 'drawer') => ({
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    flexDirection: layout === 'drawer' ? 'column' : 'row',
    ...(layout === 'footer' && {
        flexWrap: 'wrap',
        justifyContent: 'center',
        width: '100%',
    }),
});

/**
 * Header AppBar is always dark (grey[900] in light mode). Light-mode primary
 * (#0C2340) fails contrast there, so active header items inherit the AppBar
 * text color and use weight instead of primary.
 */
export const getMenuItemColor = (
    layout: MenuLayout,
    isActive: boolean,
): 'primary' | 'inherit' => {
    if (layout === 'header' || !isActive) {
        return 'inherit';
    }
    return 'primary';
};

export const getMenuItemSx = (layout: MenuLayout, isActive: boolean) => ({
    justifyContent: layout === 'drawer' ? 'flex-start' : 'center',
    ...(layout === 'header' && isActive && {
        fontWeight: 700,
    }),
});

/**
 * Orgs that get the SMSP-branded public menu on top of the default tenant itself.
 *
 * WARN: maintain this when smsp starts new tracks
 */
const SMSP_BRANDED_TENANT_SLUGS = ['smsp', 'executive'];

export const isSMSPDomain = (): boolean => {
    // getTenantSlug folds every non-tenant host label (apex, www, webapp, qa, dev,
    // beta, and their .local/.stage variants) onto 'smsp', so this no longer has to
    // enumerate hostnames — the previous substring checks missed the local and stage
    // apex hosts and any label added later.
    return SMSP_BRANDED_TENANT_SLUGS.includes(getTenantSlug().toLowerCase());
}

export const getPublicMenuItems = (isAuthenticated: boolean, layout: MenuLayout = 'drawer', isMobile: boolean) => {
    const isSMSP: boolean = isSMSPDomain();

    const menuItems: { path: string; icon: React.ElementType; label: string }[] = [];
    if (!isAuthenticated) {
        menuItems.push({ path: "/account/sms?intent=signup", icon: AppRegistration, label: "Sign Up" });
    }

    if (layout === 'drawer') {
        if (!isAuthenticated) {
            menuItems.push({ path: "/account/sms?intent=login", icon: Login, label: "Sign In" });
        }
        menuItems.push({ path: "/", icon: Foundation, label: "Home" });
    }

    if (isSMSP) {
        menuItems.push({ path: "/who-we-are", icon: InfoIcon, label: "Who We Are" });
    } else if (layout != 'header') {
        menuItems.push({ path: "/who-we-are", icon: InfoIcon, label: "Who is SMSP?" });
    }

    if (layout == 'header' && isMobile) {
        // do nothing
    } else {
        menuItems.push({ path: "/trust", icon: SecurityIcon, label: layout === 'drawer' ? "Trust, Safety & Evidence" : "Trust" });
    }

    if (layout === 'drawer') {
        menuItems.push({ path: "/contact", icon: EmailIcon, label: "Contact" });
    }
    return menuItems;
}

const PublicPagesMenu: React.FC<MenuProps> = ({ layout = 'drawer', isAuthenticated = false }) => {
    const location = useLocation();
    const { closeDrawer } = useAppContext();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const menuItems = getPublicMenuItems(isAuthenticated, layout, isMobile);

    const renderItems = () => {
        return menuItems.map(({ path, icon: Icon, label }) => {
            const isActive = path.includes("?")
                ? `${location.pathname}${location.search}` === path
                : location.pathname === path;
            return (
                <MenuButton
                    key={path}
                    component={Link}
                    to={path}
                    variant="text"
                    fullWidth={layout === 'drawer'}
                    startIcon={<Icon fontSize="small" />}
                    sx={getMenuItemSx(layout, isActive)}
                    color={getMenuItemColor(layout, isActive)}
                    onClick={getDrawerClickHandler(layout, closeDrawer)}
                >
                    {label}
                </MenuButton>
            );
        });
    };

    return <Box sx={getMenuWrapperSx(layout)}>{renderItems()}</Box>;
};

export default PublicPagesMenu;
